import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_FULL_NAME, THEME } from '../../config/brand';
import heroBg from '../../assets/images/hero/hero-students.png';

const { navy, gold, goldDark, cream, muted } = THEME;

/** Cream hero under overlapping navbar — clean typography, filled composition */
const HomeHero: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mounted, setMounted] = React.useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section
      id="hero"
      aria-labelledby="hero-heading"
      className="relative isolate min-h-[100svh] w-full overflow-hidden"
      style={{ background: cream }}
      dir="rtl"
    >
      <style>{`
        @keyframes hero-rise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-rise { animation: hero-rise 0.75s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .hero-rise-1 { animation: hero-rise 0.75s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both; }
        .hero-rise-2 { animation: hero-rise 0.75s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both; }
        .hero-rise-3 { animation: hero-rise 0.75s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both; }

        .hero-copy {
          direction: rtl;
          text-align: right;
          unicode-bidi: isolate;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-rise, .hero-rise-1, .hero-rise-2, .hero-rise-3 {
            animation: none !important;
          }
        }
      `}</style>

      <img
        src={heroBg}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[18%_center] sm:object-left lg:scale-[1.06] lg:origin-left xl:scale-[1.1]"
        draggable={false}
      />

      <div
        className="pointer-events-none absolute inset-0 hidden lg:block"
        aria-hidden
        style={{
          background: `linear-gradient(to left, ${cream} 0%, rgba(245,241,232,0.92) 34%, rgba(245,241,232,0.45) 56%, transparent 76%)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 lg:hidden"
        aria-hidden
        style={{
          background: `linear-gradient(180deg, rgba(245,241,232,0.78) 0%, rgba(245,241,232,0.88) 42%, rgba(245,241,232,0.58) 100%)`,
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[92rem] items-center px-5 pb-12 pt-[5.75rem] sm:px-8 sm:pb-14 sm:pt-[6rem] lg:px-12 lg:pt-[6.5rem] xl:px-16">
        <div
          className="hero-copy flex w-full max-w-xl flex-col justify-center lg:max-w-[42rem] xl:max-w-[46rem]"
          dir="rtl"
          lang="ar"
        >
          <p
            className="hero-rise mb-4 text-sm font-semibold tracking-[0.04em] sm:mb-5 sm:text-base"
            style={{ color: goldDark }}
          >
            لكل المراحل · لكل المواد
          </p>

          <h1
            id="hero-heading"
            className="hero-rise-1 font-cairo-bold text-[clamp(2.35rem,6vw+0.7rem,4.6rem)] leading-[1.15]"
            style={{ color: navy }}
          >
            {BRAND_FULL_NAME}
          </h1>

          <p
            className="hero-rise-1 mt-4 max-w-[28rem] text-[clamp(1.15rem,1.8vw+0.55rem,1.65rem)] font-bold leading-snug sm:mt-5"
            style={{ color: navy }}
          >
            أول منصة شاملة لطلاب مصر
          </p>

          <div
            className="hero-rise-2 mt-5 h-1 w-16 rounded-full sm:mt-6 sm:w-20"
            style={{ background: gold }}
            aria-hidden
          />

          <p
            className="hero-rise-2 mt-5 max-w-[32rem] text-[clamp(0.95rem,0.55vw+0.75rem,1.15rem)] leading-[1.85] sm:mt-6"
            style={{ color: muted }}
          >
            منصة تعليم أونلاين تجمع كل المراحل الدراسية وكل المواد في مكان واحد —
            من الابتدائي للإعدادي والثانوي، مع مدرّسين متخصصين وشرح واضح ومراجعات
            تساعدك تذاكر بثقة في أي وقت ومن أي جهاز.
          </p>

          <div className="hero-rise-3 mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            {mounted && (
              <button
                type="button"
                onClick={() => navigate(isAuthenticated ? '/student-portal' : '/register')}
                className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full px-8 text-base font-cairo-bold text-white transition hover:brightness-105 sm:min-h-[56px] sm:w-auto sm:px-9"
                style={{ background: gold, boxShadow: '0 10px 28px rgba(192,144,72,0.35)' }}
              >
                {isAuthenticated ? 'ادخل لمنصتك' : 'سجّل الآن مجانًا'}
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                document.getElementById('teachers-section')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full border-2 bg-white/85 px-7 text-base font-semibold backdrop-blur-sm transition hover:bg-white sm:min-h-[56px] sm:w-auto sm:px-8"
              style={{ borderColor: navy, color: navy }}
            >
              <Users className="h-4 w-4" aria-hidden />
              اعرف المدرسين
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
